import {BasePoint} from '../models/base-point';
import {BasePointEnum} from '../enums/base-point.enum';
import {SphericalUtil} from './spherical-util';
import {fitPlane} from '../map3d/textured-mesh.util';
import type {Polygon} from '../models/polygon';
import type {PolygonPoint} from '../models/polygon-point';
import {PolygonShape} from '../enums/polygon-shape.enum';
import {UnitConverter} from './unit-converter';

/**
 * Measurements that take height into account.
 *
 * The flat figures (area, perimeter, side lengths) are measured on the
 * ellipsoid, as if every corner sat at sea level. On a slope -- a roof plane,
 * a field on a hillside, a ramp -- the real surface is larger and its edges
 * longer. Each corner's height is its `altitude` (the surface under it, above
 * sea level: the height clicked in the 3D view, else the Elevation API's
 * ground) plus `altitude3d` (a height set above that by hand).
 *
 * - A length runs along the slope: each side is the hypotenuse of its flat
 *   length and the rise between its two corners.
 * - An area is the flat area tilted onto the plane that best fits the
 *   corners. That is exact for a flat surface -- one roof plane, a field on
 *   an even slope -- which is why a roof is traced plane by plane: one outline
 *   around a whole hip roof sits level at the eaves and measures its footprint.
 */
export interface Measure3d {
  /** Perimeter of a closed shape, or the length of a line, along the slope (m). */
  length: number;
  /** Area on the best-fit plane (m²); null for a line. */
  area: number | null;
  /** That plane's steepest slope in degrees; null for a line. */
  slope: number | null;
}

/** Whether a point's `altitude` is a real elevation: 0 and the NO_* markers mean "not looked up yet". */
export function hasHeight(point: BasePoint): boolean {
  const altitude = point?.altitude;
  return typeof altitude === 'number' && isFinite(altitude) && altitude !== 0
    && altitude !== BasePointEnum.NO_FIRST_ALTITUDE && altitude !== BasePointEnum.NO_ALTITUDE;
}

/**
 * Where a point's height is read from. A closed shape's closing point is its
 * first corner again, so it stands at that corner's height whatever it holds
 * itself: closing points were saved with the ground's height under a corner
 * clicked on a roof, which ran the closing side from the roof to the ground.
 */
function heightSource(point: BasePoint): BasePoint {
  const points = (point as PolygonPoint)?.parent?.points;
  const last = points ? points.length - 1 : -1;
  return last > 0 && points[last] === point && points[0].equalsPosition(point) ? points[0] : point;
}

/** A shape's corners: its points without the closing point, which repeats the first. */
function cornersOf(polygon: Polygon): BasePoint[] {
  const points = polygon.points;
  return points.length > 1 && polygon.isClosed() ? points.slice(0, -1) : points;
}

/** A point's height above sea level, or null when it is not known. */
export function heightOf(point: BasePoint): number | null {
  const source = heightSource(point);
  return hasHeight(source) ? source.altitude + (source.altitude3d || 0) : null;
}

/** One side along the slope, from its flat length; null unless both ends have a height. */
export function slopeLength(flatDistance: number, from: BasePoint, to: BasePoint): number | null {
  const a = heightOf(from);
  const b = heightOf(to);
  return a === null || b === null ? null : Math.hypot(flatDistance, b - a);
}

/** How steeply one side climbs or falls, in degrees (unsigned); null unless both ends have a height. */
export function sideSlope(flatDistance: number, from: BasePoint, to: BasePoint): number | null {
  const a = heightOf(from);
  const b = heightOf(to);
  if (a === null || b === null) {
    return null;
  }
  return flatDistance > 0 ? Math.atan(Math.abs(b - a) / flatDistance) * 180 / Math.PI : 90;
}

/** Whether a shape can be measured in 3D at all (a circle has two points and no corners). */
export function canMeasure3d(polygon: Polygon): boolean {
  return !!polygon && polygon.shape !== PolygonShape.CIRCLE && polygon.points.length >= 2;
}

/** The 3D figures, or null until every corner has a height. */
export function measure3d(polygon: Polygon): Measure3d | null {
  if (!canMeasure3d(polygon)) {
    return null;
  }
  const points = polygon.points;
  // The closing point is not a corner of its own: the closing side runs back
  // to the first corner, and the plane is fitted through the corners once.
  const corners = cornersOf(polygon);
  if (!corners.every(hasHeight)) {
    return null;
  }
  const ring = corners.length < points.length ? [...corners, corners[0]]
    : polygon.measuresAsClosed() ? [...points, points[0]] : points;
  let length = 0;
  for (let i = 1; i < ring.length; i++) {
    const flat = SphericalUtil.computeDistanceBetween(ring[i - 1].latLng, ring[i].latLng);
    length += Math.hypot(flat, heightOf(ring[i]) - heightOf(ring[i - 1]));
  }
  if (!polygon.showSurface) {
    return {length, area: null, slope: null};
  }

  // Corners in metres around the first one -- local enough for a shape on
  // one site -- then the plane h = a·x + b·y + c through them.
  const origin = points[0].latLng;
  const metresPerDegLat = 111132.92 - 559.82 * Math.cos(2 * origin.lat * Math.PI / 180);
  const metresPerDegLng = 111412.84 * Math.cos(origin.lat * Math.PI / 180);
  const plane = fitPlane(corners.map(p => ({
    // The short way round: across the 180th meridian the raw difference is ~360.
    x: SphericalUtil.wrap(p.latLng.lng - origin.lng, -180, 180) * metresPerDegLng,
    y: (p.latLng.lat - origin.lat) * metresPerDegLat,
    h: heightOf(p),
  })));
  if (!plane) {
    // All corners in a line: there is no surface to tilt.
    return {length, area: polygon.area, slope: 0};
  }
  const base = plane(0, 0);
  const grade = Math.hypot(plane(1, 0) - base, plane(0, 1) - base);
  return {
    length,
    area: polygon.area * Math.sqrt(1 + grade * grade),
    slope: Math.atan(grade) * 180 / Math.PI,
  };
}

/** Whether the 3D figures differ enough from the flat ones to be worth showing beside them. */
export function differsFromFlat(polygon: Polygon, m: Measure3d | null): boolean {
  if (!m) {
    return false;
  }
  const relative = (a: number, b: number) => b > 0 && Math.abs(a - b) / b > 0.001;
  return relative(m.length, polygon.perimeter) || (m.area !== null && relative(m.area, polygon.area));
}

/** "26.6°", with the roofer's pitch ("6/12") where feet are in use. */
export function slopeText(degrees: number, imperial: boolean): string {
  const text = `${(Math.round(degrees * 10) / 10).toLocaleString(UnitConverter.locale, {maximumFractionDigits: 1})}°`;
  if (!imperial || degrees >= 89.5) {
    return text;
  }
  const rise = Math.round(Math.tan(degrees * Math.PI / 180) * 12 * 2) / 2;
  return `${text} · ${rise.toLocaleString(UnitConverter.locale, {maximumFractionDigits: 1})}/12`;
}
